import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AddressLabel, SavedAddress } from '../data/places';
import {
  deleteAddressFromServer,
  fetchAddresses,
  saveAddressToServer,
  setDefaultAddressOnServer,
  toSavedAddress,
  updateAddressOnServer,
} from '../services/bookings';
import { isNotServiceableError } from '../services/location';
import {
  loadPendingAddresses,
  savePendingAddresses,
} from '../data/storage';

/**
 * The customer's saved addresses, from the platform.
 *
 * Mirrors `useBookings` on purpose - same shape, same reasoning. Addresses
 * used to live in `App`'s own state, written to `AsyncStorage` under one key
 * shared by the whole device. That is what let a second phone number signing
 * in on the same phone see the first account's addresses: nothing was ever
 * scoped to who was actually signed in, because there was no "who" to scope
 * it to. This hook has one - `signedIn` - and re-reads whenever it changes,
 * which is what makes a fresh sign-in start from an empty book instead of
 * inheriting one.
 */
export type UseAddresses = {
  addresses: SavedAddress[];
  loading: boolean;
  /** Set when the last attempt failed. Cleared by a successful refresh. */
  error: string | null;
  /** Re-reads the book. Safe to call after a save, or on pull-to-refresh. */
  refresh: () => Promise<void>;
  save: (input: {
    label: AddressLabel;
    addressLine: string;
    landmark?: string;
    lat: number;
    lng: number;
  }) => Promise<SavedAddress>;
  /**
   * Remove one, by its server id - which is `place.id`, see `toSavedAddress`.
   *
   * THROWS on failure. The caller is expected to report it: a delete that
   * silently does nothing is the state this screen was in before, where a row
   * stayed on screen and the customer could not tell whether it had gone.
   */
  remove: (id: string) => Promise<void>;
  /** Re-label an address in place, without touching its pin. */
  rename: (id: string, label: AddressLabel) => Promise<SavedAddress>;
  /**
   * Replace an address wholesale - pin, label, line and landmark.
   *
   * What "Edit" saves. `rename` only ever touched the label, so editing an
   * address that had MOVED had nothing to call and the flow fell back to
   * inserting a second row for the same doorway.
   */
  update: (input: {
    id: string;
    label: AddressLabel;
    addressLine: string;
    landmark?: string;
    lat: number;
    lng: number;
  }) => Promise<SavedAddress>;
  /**
   * Make one address the default. THROWS on failure, like `remove` - a tap
   * that silently fails to move the badge is worse than one that reports it.
   */
  setDefault: (id: string) => Promise<void>;
};

const SERVER_LABEL: Record<AddressLabel, 'home' | 'office' | 'other'> = {
  Home: 'home',
  Work: 'office',
  Other: 'other',
};

export function useAddresses(signedIn: boolean): UseAddresses {
  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * The device's own out-of-area addresses, kept beside the server's.
   *
   * Held in its own state rather than merged into `addresses` at write time
   * so the two can be refreshed independently: a server fetch replaces the
   * server rows wholesale, and doing that to a merged list would drop every
   * local one on each refresh.
   */
  const [pending, setPending] = useState<SavedAddress[]>([]);

  /* Read once. These only change when this hook writes them, and it updates
     `pending` itself at the same time. */
  useEffect(() => {
    loadPendingAddresses()
      .then(found => {
        if (alive.current) {
          setPending(found);
        }
      })
      .catch(() => {
        /* An unreadable file means no local addresses, which is the same
           state as never having saved one. Nothing to report. */
      });
  }, []);

  const load = useCallback(async () => {
    if (!signedIn) {
      setAddresses([]);
      setError(null);
      return;
    }

    setLoading(true);
    try {
      const rows = await fetchAddresses();
      if (!alive.current) {
        return;
      }
      setAddresses(rows.map(toSavedAddress));
      setError(null);
    } catch (failure) {
      if (alive.current) {
        setError(
          failure instanceof Error
            ? failure.message
            : 'Could not load your saved addresses.',
        );
      }
    } finally {
      if (alive.current) {
        setLoading(false);
      }
    }
  }, [signedIn]);

  /* Signing in and out is the one thing that changes whose book this is. */
  useEffect(() => {
    load().catch(() => {
      /* `load` records its own failure in `error`; nothing else to do with
         a rejection here. */
    });
  }, [load]);

  const save = useCallback<UseAddresses['save']>(async input => {
    let row;
    try {
      row = await saveAddressToServer({
        label: SERVER_LABEL[input.label],
        addressLine: input.addressLine,
        landmark: input.landmark,
        pinLat: input.lat,
        pinLng: input.lng,
      });
    } catch (failure) {
      /*
       * Refused because we do not serve that city yet - keep it anyway.
       *
       * ---------------------------------------------------------------------
       * WHY THIS IS NOT A FAILURE
       * ---------------------------------------------------------------------
       * The server answers a pin outside its cities with a 400, and until now
       * that threw all the way out to the caller, which showed an alert and
       * dropped the address on the floor. Nothing was wrong with it: the
       * customer typed their real home address and was told, in effect, to
       * forget it - and then to type it again next launch to be told the same.
       *
       * So it is kept on the device instead and returned as though the save
       * had worked, because from the customer's side it has: the address is
       * in their book, it is theirs, and the only difference is the "Coming
       * soon" marker on the row and the block at checkout that was already
       * there.
       *
       * ONLY for the not-serviceable rejection. Every other failure - no
       * network, a 401, a malformed line - still throws, because those are
       * real failures where a silent local save would hide a bug and leave
       * the customer believing the platform has an address it does not.
       */
      if (!isNotServiceableError(failure)) {
        throw failure;
      }

      const local: SavedAddress = {
        /*
         * A local id, prefixed so nothing mistakes it for the server's.
         * `localOnly` is the flag that is actually checked - this prefix is
         * for the human reading a log.
         *
         * Built from the pin rather than a counter: saving the same doorway
         * twice should land on the same id and replace the row, exactly as
         * the server's own upsert would.
         */
        place: {
          id: `local:${input.lat.toFixed(5)},${input.lng.toFixed(5)}`,
          title: input.addressLine.split(',')[0]?.trim() || input.addressLine,
          subtitle: input.addressLine,
          coords: { lat: input.lat, lng: input.lng },
        },
        coords: { lat: input.lat, lng: input.lng },
        label: input.label,
        note: input.landmark,
        /* Never the default. The default is what a booking falls back to, and
           falling back to an address we cannot serve would push the coverage
           wall onto someone who never chose it. */
        isDefault: false,
        serviceable: false,
        localOnly: true,
      };

      if (alive.current) {
        setPending(current => {
          const next = [
            local,
            ...current.filter(item => item.place.id !== local.place.id),
          ];
          savePendingAddresses(next);
          return next;
        });
      }

      return local;
    }

    const saved = toSavedAddress(row);

    /* Put it straight in rather than waiting for a refetch, the same reason
       `useBookings.book` does - the customer is about to be shown it. */
    if (alive.current) {
      setAddresses(current => [
        saved,
        ...current.filter(item => item.place.id !== saved.place.id),
      ]);
    }
    return saved;
  }, []);

  const remove = useCallback<UseAddresses['remove']>(async id => {
    /*
     * A local row has no server id, so sending it to the delete endpoint
     * would 404 and leave the row on screen - the exact "tap does nothing"
     * state `remove`'s own contract exists to prevent. Dropped from the
     * device instead, which for these IS the delete.
     */
    if (id.startsWith('local:')) {
      if (alive.current) {
        setPending(current => {
          const next = current.filter(item => item.place.id !== id);
          savePendingAddresses(next);
          return next;
        });
      }
      return;
    }

    await deleteAddressFromServer(id);

    /* Dropped locally rather than refetched, the same reason `save` splices
       its result in - the row is gone and the list should say so at once. */
    if (alive.current) {
      setAddresses(current => current.filter(item => item.place.id !== id));
    }
  }, []);

  const rename = useCallback<UseAddresses['rename']>(async (id, label) => {
    const row = await updateAddressOnServer(id, { label: SERVER_LABEL[label] });
    const updated = toSavedAddress(row);

    if (alive.current) {
      setAddresses(current =>
        current.map(item => (item.place.id === id ? updated : item)),
      );
    }
    return updated;
  }, []);

  const update = useCallback<UseAddresses['update']>(async input => {
    const row = await updateAddressOnServer(input.id, {
      label: SERVER_LABEL[input.label],
      addressLine: input.addressLine,
      landmark: input.landmark,
      pinLat: input.lat,
      pinLng: input.lng,
    });
    const updated = toSavedAddress(row);

    /* Replaced in place rather than appended - the whole point of an edit is
       that the book does not grow a second copy of the same address. */
    if (alive.current) {
      setAddresses(current =>
        current.map(item => (item.place.id === input.id ? updated : item)),
      );
    }
    return updated;
  }, []);

  const setDefault = useCallback<UseAddresses['setDefault']>(async id => {
    await setDefaultAddressOnServer(id);

    /* Flipped locally rather than refetched - same reason `remove` splices
       rather than reloads. The server clears the flag on every other row,
       so the local copy has to do the same or the old default's badge would
       linger until the next full refresh. */
    if (alive.current) {
      setAddresses(current =>
        current.map(item => ({
          ...item,
          isDefault: item.place.id === id,
        })),
      );
    }
  }, []);

  /*
   * Server rows first, device-only ones after.
   *
   * Ordered rather than interleaved by date on purpose: an address we cannot
   * serve is not one the customer is about to book against, so it belongs
   * below the ones they can use, not competing with them at the top of the
   * picker.
   *
   * Merged here at read time rather than kept in one array, so a `refresh`
   * can replace the server half wholesale without touching the local half.
   */
  const merged = useMemo(
    () => [...addresses, ...pending],
    [addresses, pending],
  );

  return {
    addresses: merged,
    loading,
    error,
    refresh: load,
    save,
    remove,
    rename,
    update,
    setDefault,
  };
}
